import React, { useEffect, useState } from 'react'
import DOCUMENT_DEFAULT from '../../../../public/assets/document_default.svg'
import SUMMARY_DEFAULT from '../../../../public/assets/summary_default.svg'
import SUMMARY_IMAGE from '../../../../public/assets/summary_image.svg'
import { buildFullURL, getBaseURL } from '../../../../api/axiosTemp'
import BaseButton from '../../../components/common/BaseButton'
import { FloatingWindow } from '../../../components/common/FloatingWindow'
import { PDFPreviewWindow } from '../../../components/common/PDFPreviewWindow'
import { PDFViewer } from '@progress/kendo-react-pdf-viewer'
import { Tooltip } from '@progress/kendo-react-tooltip'
import type { NarrativeOcgDocument, NarrativeOcgValidationHint } from '../types'
import { hasOcgValidationFindings } from '../utils/issueDetails'
import { MarkdownPreview } from './MarkdownPreview'
import { NarrativeOcgMobileFullScreen } from './NarrativeOcgMobileFullScreen'

const referenceTooltipStyle: React.CSSProperties = {
  backgroundColor: '#292929',
  border: 'none',
  color: '#fff',
}

const resolveDocumentUrl = (url?: string) => {
  if (!url) return ''
  if (/^(https?:|data:|blob:)/.test(url)) return url

  const customBaseURL = getBaseURL()
  return buildFullURL(customBaseURL || window.location.origin, url)
}

const getPrimaryOcgDocument = (
  validationHint?: NarrativeOcgValidationHint
): NarrativeOcgDocument | undefined =>
  validationHint?.ocgDocument ?? validationHint?.ocgDocuments?.[0] ?? undefined

const getOcgDocumentTitle = (document?: NarrativeOcgDocument) =>
  document?.original_filename || document?.doc_uuid || 'View Document'

const getOcgDocumentUrl = (document?: NarrativeOcgDocument) =>
  resolveDocumentUrl(document?.original_file_url || document?.document_url)

const OcgSummaryContent = ({
  document,
  summary,
}: {
  document?: NarrativeOcgDocument
  summary: string
}) => {
  return (
    <>
      {summary ? (
        document?.summary_format === 'html' ? (
          <div
            className="narrative-ocg-summary-content"
            dangerouslySetInnerHTML={{ __html: summary }}
          />
        ) : (
          <MarkdownPreview value={summary} />
        )
      ) : (
        <div className="narrative-ocg-summary-empty">No summary available</div>
      )}
    </>
  )
}

export const NarrativeOcgReferenceActions = ({
  validationHint,
  onFloatingWindowInteraction,
  isMobile = false,
}: {
  validationHint?: NarrativeOcgValidationHint
  onFloatingWindowInteraction?: (isOpen?: boolean) => void
  isMobile?: boolean
}) => {
  const [openDocument, setOpenDocument] = useState(false)
  const [openSummary, setOpenSummary] = useState(false)
  const document = getPrimaryOcgDocument(validationHint)
  const documentUrl = getOcgDocumentUrl(document)
  const summary = document?.summary || ''

  useEffect(() => {
    if (!openSummary) return

    onFloatingWindowInteraction?.(true)

    return () => {
      onFloatingWindowInteraction?.(false)
    }
  }, [openSummary, onFloatingWindowInteraction])

  if (!hasOcgValidationFindings(validationHint)) return null

  return (
    <>
      <Tooltip
        anchorElement="target"
        parentTitle
        position="top"
        tooltipStyle={referenceTooltipStyle}
      >
        <div
          className="narrative-ocg-panel__view-switch"
          aria-label="Narrative reference views"
        >
          <BaseButton
            className="narrative-ocg-panel__view-button !min-h-0 !bg-transparent !p-0 shadow-none"
            fillMode="flat"
            onClick={event => {
              onFloatingWindowInteraction?.()
              event.preventDefault()
              event.stopPropagation()
              setOpenDocument(true)
            }}
            themeColor="base"
            title="View Document"
            type="button"
            aria-label="View Document"
          >
            <img src={DOCUMENT_DEFAULT} alt="" />
          </BaseButton>
          <span className="narrative-ocg-panel__view-divider" />
          <BaseButton
            className="narrative-ocg-panel__view-button !min-h-0 !bg-transparent !p-0 shadow-none"
            fillMode="flat"
            onClick={event => {
              onFloatingWindowInteraction?.()
              event.preventDefault()
              event.stopPropagation()
              setOpenSummary(true)
            }}
            themeColor="base"
            title="OCG Document"
            type="button"
            aria-label="OCG Document"
          >
            <img src={SUMMARY_DEFAULT} alt="" />
          </BaseButton>
        </div>
      </Tooltip>

      {isMobile && (
        <>
          {openDocument ? (
            <NarrativeOcgMobileFullScreen
              title="View Document"
              onClose={() => {
                onFloatingWindowInteraction?.()
                setOpenDocument(false)
              }}
              onInteraction={onFloatingWindowInteraction}
            >
              {documentUrl ? (
                <div className="narrative-ocg-mobile-document">
                  <div className="narrative-ocg-mobile-document__preview">
                    <PDFViewer
                      url={documentUrl}
                      defaultZoom={0.5}
                      onRenderToolbar={() => null}
                      style={{ height: '100%', width: '100%' }}
                    />
                  </div>
                  <a
                    className="narrative-ocg-mobile-document__download"
                    href={documentUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={event => {
                      onFloatingWindowInteraction?.()
                      event.stopPropagation()
                    }}
                  >
                    Download
                  </a>
                </div>
              ) : (
                <div className="narrative-ocg-summary-empty">
                  No PDF available
                </div>
              )}
            </NarrativeOcgMobileFullScreen>
          ) : null}
          {openSummary ? (
            <NarrativeOcgMobileFullScreen
              title="OCG Document"
              onClose={() => {
                onFloatingWindowInteraction?.()
                setOpenSummary(false)
              }}
              onInteraction={onFloatingWindowInteraction}
            >
              <OcgSummaryContent document={document} summary={summary} />
            </NarrativeOcgMobileFullScreen>
          ) : null}
        </>
      )}

      {!isMobile && (
        <div
          onClick={event => event.stopPropagation()}
          onMouseDown={event => event.stopPropagation()}
          onTouchStart={event => event.stopPropagation()}
        >
          <PDFPreviewWindow
            visible={openDocument}
            onClose={() => {
              onFloatingWindowInteraction?.()
              setOpenDocument(false)
            }}
            title={getOcgDocumentTitle(document)}
            pdfUrl={documentUrl}
            width={720}
            height={500}
            hideMinimizeButton
            hideMaximizeButton
            useCustomActions
            onActionInteraction={onFloatingWindowInteraction}
            className="narrative-ocg-reference-window"
          />
          <FloatingWindow
            visible={openSummary}
            onClose={() => {
              onFloatingWindowInteraction?.()
              setOpenSummary(false)
            }}
            title="OCG Document"
            headerIcon={SUMMARY_IMAGE}
            width={720}
            height={460}
            draggable
            resizable
            hideMinimizeButton
            useCustomActions
            showCustomMaximizeButton
            onActionInteraction={onFloatingWindowInteraction}
            modal
            overlayStyle={{
              backgroundColor: '#000',
              opacity: 0.2,
              zIndex: 1000000,
            }}
            className="narrative-ocg-reference-window"
          >
            <OcgSummaryContent document={document} summary={summary} />
          </FloatingWindow>
        </div>
      )}
    </>
  )
}
